/**
 * Central GSAP setup. Import GSAP + plugins from here (never from 'gsap'
 * directly) so plugin registration and the house eases/defaults are guaranteed.
 *
 * GSAP 3.13+ ships the full plugin set free in the public npm package, so
 * ScrollTrigger, SplitText, DrawSVG, MorphSVG, Flip, Draggable, Observer,
 * ScrollSmoother and ScrollTo are all available.
 */
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { Observer } from 'gsap/Observer';
import { Draggable } from 'gsap/Draggable';
import { Flip } from 'gsap/Flip';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
import { MorphSVGPlugin } from 'gsap/MorphSVGPlugin';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { CustomEase } from 'gsap/CustomEase';

import { DURATION, easePath, EASE, GSAP_EASE } from './motion-tokens';

let registered = false;

/**
 * Idempotently register every plugin, publish the house CustomEases and set the
 * global defaults. Safe to call from any component; runs its body only once.
 */
export function registerGsap(): typeof gsap {
  if (registered || typeof window === 'undefined') return gsap;
  registered = true;

  gsap.registerPlugin(
    useGSAP,
    ScrollTrigger,
    SplitText,
    Observer,
    Draggable,
    Flip,
    DrawSVGPlugin,
    MorphSVGPlugin,
    ScrollToPlugin,
    CustomEase,
  );

  // Publish the brand curves so any tween can use ease: 'ibas-out', etc.
  CustomEase.create(GSAP_EASE.out, easePath(EASE.out));
  CustomEase.create(GSAP_EASE.inOut, easePath(EASE.inOut));
  CustomEase.create(GSAP_EASE.in, easePath(EASE.in));
  CustomEase.create(GSAP_EASE.spring, easePath(EASE.spring));

  gsap.defaults({ ease: GSAP_EASE.out, duration: DURATION.base });

  // A gentle, consistent ScrollTrigger baseline across the app.
  ScrollTrigger.config({ ignoreMobileResize: true });

  return gsap;
}

// Register eagerly at import time. Any module that touches GSAP does so through
// this file, so plugins (ScrollTrigger, SplitText, …) and the house eases are
// guaranteed to be registered before a component's effect creates a trigger —
// avoiding the "_context is not a function" error when a child effect runs
// before the provider's effect.
registerGsap();

export {
  gsap,
  useGSAP,
  ScrollTrigger,
  SplitText,
  Observer,
  Draggable,
  Flip,
  DrawSVGPlugin,
  MorphSVGPlugin,
  ScrollToPlugin,
  CustomEase,
};
